import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../CustomCodes.redux';
import { Grid, TextField } from '@mui/material';
import DraggableCodeCard from 'components/DraggableCodeCard';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useDebounce } from '@uidotdev/usehooks';

const grid = 8,
    getListStyle = (isDraggingOver, isEmpty = false) => ({
        background: isDraggingOver && isEmpty ? 'rgba(0, 0, 0, 0.12)' : undefined,
        padding: `${grid}px 0 ${grid}px 0`,
        width: '100%',
        maxHeight: 600,
        overflowY: 'auto',
        ...(isEmpty
            ? {
                  marginTop: '16px',
                  height: '200px',
                  border: '2px dashed rgba(0, 0, 0, 0.35)',
                  borderRadius: '16px',
              }
            : {}),
    }),
    getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: 'none',
        padding: `${grid}px 8px ${grid}px 0`,
        margin: '0',
        ...draggableStyle,
    }),
    move = (source, destination, droppableSource, droppableDestination) => {
        const sourceClone = Array.from(source);
        const destClone = Array.from(destination);
        const [removed] = sourceClone.splice(droppableSource.index, 1);

        destClone.splice(droppableDestination.index, 0, removed);

        const result = {};
        result[droppableSource.droppableId] = sourceClone;
        result[droppableDestination.droppableId] = destClone;

        return result;
    },
    reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

const DragDropContainer = ({ index }) => {
    const dispatch = useDispatch(),
        [itemDroppableKey] = useState(`items_${index}_droppable`),
        [selectedDroppableKey] = useState(`selected_${index}_droppable`),
        codes = useSelector((state) => state.customCodes.multiSelectCodes),
        allSelected = useSelector((state) => state.customCodes.multiSelectAllSelected),
        options = useSelector((state) => state.customCodes.conf.symbol_chart_config.options[index].codes);

    const [state, setState] = useState({
        items: [],
        selected: [],
    });

    const [stringFilter, setStringFilter] = useState('');
    const stringFilterDebounced = useDebounce(stringFilter, 300);

    const filteredItems = useMemo(() => {
        const q = stringFilterDebounced.trim().toLowerCase();
        if (!q) return state.items;
        return state.items.filter((item) => item.content.toLowerCase().includes(q));
    }, [state.items, stringFilterDebounced]);

    const handleDragEnd = ({ source, destination }) => {
        if (!destination) {
            return;
        }

        let newState;

        if (source.droppableId === destination.droppableId) {
            // Same-list reorder
            if (source.droppableId === selectedDroppableKey) {
                newState = { ...state, selected: reorder(state.selected, source.index, destination.index) };
            } else {
                // Reorder within the right list — operate on filteredItems indices then reconcile full items
                const reordered = reorder(filteredItems, source.index, destination.index);
                const filteredIds = new Set(filteredItems.map((i) => i.id));
                const unfiltered = state.items.filter((i) => !filteredIds.has(i.id));
                newState = { ...state, items: [...reordered, ...unfiltered] };
            }
        } else if (source.droppableId === itemDroppableKey) {
            // Right → Left: source index refers to filteredItems, not state.items
            const draggedItem = filteredItems[source.index];
            const newSelected = Array.from(state.selected);
            newSelected.splice(destination.index, 0, draggedItem);
            newState = {
                items: state.items.filter((item) => item.id !== draggedItem.id),
                selected: newSelected,
            };
        } else {
            // Left → Right: selected is never filtered, so indices are correct
            const result = move(state.selected, state.items, source, destination);
            newState = {
                items: result[itemDroppableKey],
                selected: result[selectedDroppableKey],
            };
        }

        dispatch(
            actions.setMultiSelectCodes(
                index,
                newState.selected.map((row) => row.symbol_id)
            )
        );
        setState(newState);
    };

    // Manage local state based on redux state
    useEffect(() => {
        setState({
            items: codes
                .filter((row) => !allSelected.includes(row.symbol_id) && !options.includes(row.symbol_id))
                .map((code) => ({
                    id: `symbol-${code.symbol_id}`,
                    content: `${code.symbol_name} (${code.symbol_abbreviation})`,
                    symbol_id: code.symbol_id,
                    symbol_category: code.symbol_category,
                    clinic_id: code.clinic_id,
                    type: code.symbol_chart_config?.type ?? null,
                })),

            selected: options
                .map((id) => {
                    let code = codes.find((row) => row.symbol_id === id);
                    if (!code) {
                        return null;
                    }

                    return {
                        id: `symbol-${code.symbol_id}`,
                        content: `${code.symbol_name} (${code.symbol_abbreviation})`,
                        symbol_id: code.symbol_id,
                        symbol_category: code.symbol_category,
                        clinic_id: code.clinic_id,
                        type: code.symbol_chart_config?.type ?? null,
                    };
                })
                .filter((row) => row !== null),
        });
    }, [setState, codes, options, allSelected]);

    return (
        <div style={{ width: '100%' }}>
            <Grid container spacing={4}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Grid size={6}>
                        <Droppable droppableId={selectedDroppableKey}>
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    style={{
                                        ...getListStyle(snapshot.isDraggingOver, state.selected.length === 0),
                                        ...(state.selected.length === 0
                                            ? { display: 'flex', alignItems: 'center', justifyContent: 'center' }
                                            : {}),
                                    }}
                                >
                                    {state.selected.length === 0 && !snapshot.isDraggingOver && (
                                        <span
                                            style={{
                                                color: 'rgba(0,0,0,0.4)',
                                                fontSize: '0.875rem',
                                                textAlign: 'center',
                                                padding: '0 16px',
                                            }}
                                        >
                                            Drag and drop codes here to add them to this multi-select option.
                                        </span>
                                    )}
                                    {state.selected.map((item, index) => (
                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                >
                                                    <DraggableCodeCard code={item} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Grid>

                    <Grid size={6}>
                        <TextField
                            placeholder="Search codes"
                            size="small"
                            fullWidth
                            value={stringFilter}
                            onChange={(e) => setStringFilter(e.target.value)}
                            sx={{ mb: 1 }}
                        />
                        <Droppable droppableId={itemDroppableKey}>
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                                    {filteredItems.map((item, index) => (
                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                >
                                                    <DraggableCodeCard code={item} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Grid>
                </DragDropContext>
            </Grid>
        </div>
    );
};

export default DragDropContainer;
